import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, createVNode as _createVNode } from "vue"

const _hoisted_1 = { class: "space-y-6" }
const _hoisted_2 = { class: "bg-white p-6 rounded-lg shadow" }
const _hoisted_3 = { class: "grid grid-cols-1 md:grid-cols-3 gap-4" }
const _hoisted_4 = { class: "p-4 rounded-lg text-center" }
const _hoisted_5 = {
  key: 0,
  class: "text-3xl font-bold"
}
const _hoisted_6 = {
  key: 1,
  class: "text-3xl font-bold"
}
const _hoisted_7 = { class: "p-4 rounded-lg text-center" }
const _hoisted_8 = {
  key: 0,
  class: "text-3xl font-bold"
}
const _hoisted_9 = {
  key: 1,
  class: "text-3xl font-bold"
}
const _hoisted_10 = {
  key: 2,
  class: "text-sm text-gray-500"
}
const _hoisted_11 = { class: "p-4 rounded-lg text-center" }
const _hoisted_12 = {
  key: 1,
  class: "text-3xl font-bold"
}
const _hoisted_13 = { class: "bg-white p-6 rounded-lg shadow" }

import { computed, ref, onMounted } from 'vue';
import CopperPriceChart from '../components/Chart.vue';
import { getCopperPriceHistory } from '@/services/copperPrice.service';
import { roundStringToDecimals } from '@/utils/priceListUtils';
import { getIsoDate } from '@/utils/copperPriceUtils';
import { dispatchToast } from '@/utils/dispatchToast';


export default /*@__PURE__*/_defineComponent({
  __name: 'CopperPriceAnalysis',
  props: {
    oldCopperPrice: {},
    copperPrice: {},
    dates: {}
  },
  setup(__props: any) {

const props = __props;

const copperPercentageChange = computed(() => {
  if (!props.oldCopperPrice || !props.copperPrice) {
    return null;
  }
  return roundStringToDecimals(((props.copperPrice - props.oldCopperPrice) / props.oldCopperPrice) * 100, 2);
});

const chartData = computed(() => (weeklyAverage.value.map(item => ({ label: item.date, value: item.average }))));
const weeklyAverage = ref<{ date: string, average: number }[]>([]);

const fetchLast6MonthsCopperPrice = async () => {
  try {
    // Get the last 6 months of copper price data
    const currentDate = new Date();
    const sixMonthsAgo = new Date().setMonth(currentDate.getMonth() - 6);

    // Get the last 6 months of copper price data
    const data = await getCopperPriceHistory(getIsoDate(sixMonthsAgo), getIsoDate(currentDate));

    // Group the data by week and get the average
    weeklyAverage.value = groupByWeek(data.data.prices);
  } catch (error) {
    dispatchToast('Error fetching copper price data:', { type: 'error' });
  }
}

// Calculates next satruday in ISO string givne a date in ISO string format
const getNextSaturday = (date: string) => {
  const day = new Date(date).getDay();
  // If it's Sunday, then next saturday is 6 days from now
  let diff = day === 6 ? 6 : 6 - day;
  const nextSaturday = new Date(date);
  nextSaturday.setDate(nextSaturday.getDate() + diff);
  return getIsoDate(nextSaturday);
}

const groupByWeek = (data: { date: string, close: number }[]) => {
  // Sort data by date ascending
  const sortedData = data.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

  let endOfWeek = getNextSaturday(sortedData[0].date);
  let weekData = [];
  const weeklyAverage: { date: string, average: number }[] = [];
  // Iterate over sorted data and group by week
  for (const item of sortedData) {

    if (item.date <= endOfWeek) {
      weekData.push(item);
    } else {
      // This means we have entered a new week, so we calculate the average of the previous week, and clear the weekData
      weeklyAverage.push({ date: endOfWeek, average: weekData.reduce((acc, item) => acc + item.close, 0) / weekData.length });
      weekData = [];
      endOfWeek = getNextSaturday(item.date);
    }
  }
  return weeklyAverage;
}

onMounted(() => {
  fetchLast6MonthsCopperPrice();
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _cache[3] || (_cache[3] = _createElementVNode("div", { class: "flex justify-between items-center mb-0 pt-2 pl-2 pr-2" }, [
      _createElementVNode("h2", { class: "text-2xl font-semibold" }, "Copper Price Analysis")
    ], -1)),
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("div", _hoisted_4, [
          _cache[0] || (_cache[0] = _createElementVNode("div", { class: "text-sm text-gray-600" }, "Ref. Copper Price", -1)),
          (props.oldCopperPrice)
            ? (_openBlock(), _createElementBlock("div", _hoisted_5, "$" + _toDisplayString(props.oldCopperPrice) + "/lb", 1))
            : (_openBlock(), _createElementBlock("div", _hoisted_6, "-"))
        ]),
        _createElementVNode("div", _hoisted_7, [
          _cache[1] || (_cache[1] = _createElementVNode("div", { class: "text-sm text-gray-600" }, "New Copper Price", -1)),
          (props.copperPrice)
            ? (_openBlock(), _createElementBlock("div", _hoisted_8, "$" + _toDisplayString(props.copperPrice) + "/lb", 1))
            : (_openBlock(), _createElementBlock("div", _hoisted_9, "-")),
          (props.dates?.length > 0)
            ? (_openBlock(), _createElementBlock("div", _hoisted_10, _toDisplayString(props.dates[0]) + " → " + _toDisplayString(props.dates[props.dates.length - 1]), 1))
            : _createCommentVNode("", true)
        ]),
        _createElementVNode("div", _hoisted_11, [
          _cache[2] || (_cache[2] = _createElementVNode("div", { class: "text-sm text-gray-600" }, "Change", -1)),
          (copperPercentageChange.value)
            ? (_openBlock(), _createElementBlock("div", {
                key: 0,
                class: _normalizeClass(["text-3xl font-bold", {
            'text-green-500': copperPercentageChange.value > 0,
            'text-red-500': copperPercentageChange.value < 0,
            'text-black': copperPercentageChange.value === 0,
          }])
              }, _toDisplayString(copperPercentageChange.value > 0 ? '+' + copperPercentageChange.value : copperPercentageChange.value) + "% ", 3))
            : (_openBlock(), _createElementBlock("div", _hoisted_12, "-"))
        ])
      ])
    ]),
    _createElementVNode("div", _hoisted_13, [
      _createVNode(CopperPriceChart, { chartData: chartData.value }, null, 8, ["chartData"])
    ])
  ]))
}
}

})